import { IGatsbyImageData } from 'gatsby-plugin-image'
import { getGatsbyImageData } from 'gatsby-source-sanity'
import { ImageNode } from 'gatsby-source-sanity/lib/images/getGatsbyImageProps'
import { GatsbyImageDataArgs } from 'gatsby-source-sanity/lib-es5/images/getGatsbyImageProps'
import { identity, lensPath, set } from 'ramda'

import clientConfig from '../../../client-config'
import { SanityImageWithNodeFiltered } from '../../components/sanity-page/BannerWithText/types'
import { SanityImage } from '../../graphql/gatsby'
import { Maybe } from '../types'

/**
 * Variant of SanityImage compatible with SanityImage props (from gatsby-plugin-sanity-image)
 * as well as ImageObjectW (from gatsby-source-sanity module augmentation)
 */
export type SanityImageWithAssetId = SanityImage & {
  asset: NonNullable<SanityImage['asset']> & {
    _id: string
  }
}

/**
 * Type guard to allow spreading Sanity image into SanityImage props
 */
export function isSanityImageWithAssetId(
  value: Maybe<SanityImage> | SanityImageWithNodeFiltered,
): value is SanityImageWithAssetId {
  return !!value && !!value.asset?._id
}

/**
 * Wrapper for `getGatsbyImageData` from `gatsby-source-sanity` that uses
 * `asset` from image object. Use this instead where possible for consistent
 * look and feel due to `metadata` bug in upstream function.
 */
export const getGatsbyImageDataForAsset = (
  value: Maybe<SanityImage> | SanityImageWithNodeFiltered,
  gatsbyImageDataArgs: GatsbyImageDataArgs,
): IGatsbyImageData | null | undefined =>
  isSanityImageWithAssetId(value)
    ? getGatsbyImageData(
        // Upstream function does not destruct image object fully and `metadata`
        // is ignored unless we pass the image asset object. Also, `gatsby-source-sanity`
        // exports incomplete and overly restrictive types, making the cast necessary
        value.asset as ImageNode,
        gatsbyImageDataArgs,
        clientConfig.sanity,
      )
    : undefined

/**
 * Remove preview metadata from Sanity image asset data to show SanityImage without preview
 */
export function excludePreviewMetadata(value: SanityImageWithAssetId): SanityImageWithAssetId {
  const lqipLens = lensPath(['asset', 'metadata', 'lqip'])
  return set(lqipLens, identity(undefined), value)
}
